import React, { useState, useEffect, useRef } from 'react';
import { MdOutlineAccessibility, MdHome, MdChat, MdInfo, MdZoomIn, MdZoomOut, MdContrast, MdTextFields, MdOutlineContrast } from 'react-icons/md';
import { navigate } from 'gatsby';
import '../styles/accessibility.css';

interface ButtonInfo {
    id: string;
    icon: React.ElementType;
    path: string;
    label: string;
}

const SideButtons: React.FC = () => {
    const [hoveredButton, setHoveredButton] = useState<string | null>(null);
    const [showAccessibilityPanel, setShowAccessibilityPanel] = useState(false);
    // Arranca apagado: en el build estático no existe localStorage.
    // Las preferencias guardadas se leen al montar (useEffect más abajo).
    const [activeFeatures, setActiveFeatures] = useState({
        highContrast: false,
        textSpacing: false,
        grayscale: false
    });
    const [zoomLevel, setZoomLevel] = useState(0);
    const panelRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const buttonInfo: ButtonInfo[] = [
        /* { id: 'home', icon: MdHome, path: '/', label: 'Inicio' },
        { id: 'pqr', icon: MdChat, path: '/pqr/', label: 'PQR' },
        { id: 'nosotros', icon: MdInfo, path: '/nosotros/', label: 'Nosotros' } */
    ];

    const tooltipStyle = `
        absolute top-1/2 -translate-y-1/2 
        bg-[#e7323a] text-white 
        px-4 py-2 
        rounded-md
        shadow-[0_4px_20px_rgba(231,50,58,0.2)]
        whitespace-nowrap 
        text-sm font-medium 
        transition-all duration-300 
        transform hover:scale-105
        border border-white/10
        backdrop-blur-sm
    `;

    const accessibilityOptions = [
        { 
            id: 'zoom-in', 
            icon: MdZoomIn, 
            label: 'Aumentar texto', 
            action: () => {
                if (zoomLevel < 5) {
                    const html = document.documentElement;
                    const currentSize = window.getComputedStyle(html).fontSize;
                    const newSize = parseFloat(currentSize) * 1.1;
                    html.style.fontSize = `${newSize}px`;
                    setZoomLevel(prev => prev + 1);
                }
            }
        },
        { 
            id: 'zoom-out', 
            icon: MdZoomOut, 
            label: 'Reducir texto', 
            action: () => {
                if (zoomLevel > -5) {
                    const html = document.documentElement;
                    const currentSize = window.getComputedStyle(html).fontSize;
                    const newSize = parseFloat(currentSize) * 0.9;
                    html.style.fontSize = `${newSize}px`;
                    setZoomLevel(prev => prev - 1);
                }
            }
        },
        { 
            id: 'contrast', 
            icon: MdContrast, 
            label: 'Alto contraste', 
            action: () => {
                setActiveFeatures(prev => {
                    const newState = { ...prev, highContrast: !prev.highContrast };
                    if (newState.highContrast) {
                        document.body.classList.add('high-contrast');
                        localStorage.setItem('highContrast', 'true');
                    } else {
                        document.body.classList.remove('high-contrast');
                        localStorage.setItem('highContrast', 'false');
                    }
                    return newState;
                });
            }
        },
        { 
            id: 'grayscale', 
            icon: MdOutlineContrast, 
            label: 'Escala de grises', 
            action: () => {
                setActiveFeatures(prev => {
                    const newState = { ...prev, grayscale: !prev.grayscale };
                    if (newState.grayscale) {
                        document.body.classList.add('grayscale');
                        localStorage.setItem('grayscale', 'true');
                    } else {
                        document.body.classList.remove('grayscale');
                        localStorage.setItem('grayscale', 'false');
                    }
                    return newState;
                });
            }
        },
        { 
            id: 'text-spacing', 
            icon: MdTextFields, 
            label: 'Espaciado de texto', 
            action: () => {
                setActiveFeatures(prev => {
                    const newState = { ...prev, textSpacing: !prev.textSpacing };
                    if (newState.textSpacing) {
                        document.body.classList.add('text-spacing');
                        localStorage.setItem('textSpacing', 'true');
                    } else {
                        document.body.classList.remove('text-spacing');
                        localStorage.setItem('textSpacing', 'false');
                    }
                    return newState;
                });
            }
        }
    ];

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (showAccessibilityPanel && 
                panelRef.current && 
                !panelRef.current.contains(target) && 
                buttonRef.current && 
                !buttonRef.current.contains(target)) {
                setShowAccessibilityPanel(false);
            }
        };

        if (showAccessibilityPanel) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showAccessibilityPanel]);

    useEffect(() => {
        const saved = {
            highContrast: localStorage.getItem('highContrast') === 'true',
            textSpacing: localStorage.getItem('textSpacing') === 'true',
            grayscale: localStorage.getItem('grayscale') === 'true'
        };
        setActiveFeatures(saved);
        if (saved.highContrast) {
            document.body.classList.add('high-contrast');
        }
        if (saved.grayscale) {
            document.body.classList.add('grayscale');
        }
        if (saved.textSpacing) {
            document.body.classList.add('text-spacing');
        }
    }, []);

    return (
        <>
            {/* Botón de accesibilidad (arriba) */}
            <div className="fixed left-0 top-52 z-30">
                <button 
                    ref={buttonRef}
                    className="bg-white shadow-lg w-12 h-12 flex items-center justify-center hover:bg-red-100 transition-all duration-300 relative group border-r border-gray-100"
                    onMouseEnter={() => setHoveredButton('accesibilidad')}
                    onMouseLeave={() => setHoveredButton(null)}
                    onClick={() => setShowAccessibilityPanel(!showAccessibilityPanel)}
                >
                    <MdOutlineAccessibility size={24} className="text-[#e7323a]" />
                    {hoveredButton === 'accesibilidad' && !showAccessibilityPanel && (
                        <div className={`${tooltipStyle} left-full ml-2 hidden md:block`}>
                            Accesibilidad
                        </div>
                    )}
                </button>

                {/* Panel de accesibilidad */}
                {showAccessibilityPanel && (
                    <div ref={panelRef} className="absolute left-full top-0 bg-white shadow-xl p-4 min-w-[200px] border border-gray-100">
                        <h3 className="text-blue-900 font-semibold mb-3">Opciones de accesibilidad</h3>
                        <div className="space-y-2">
                            {accessibilityOptions.map(({ id, icon: Icon, label, action }) => (
                                <button
                                    key={id}
                                    onClick={action}
                                    disabled={
                                        (id === 'zoom-in' && zoomLevel >= 5) ||
                                        (id === 'zoom-out' && zoomLevel <= -5)
                                    }
                                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-red-50 transition-colors text-gray-700 ${
                                        (id === 'contrast' && activeFeatures.highContrast) || 
                                        (id === 'text-spacing' && activeFeatures.textSpacing) ||
                                        (id === 'grayscale' && activeFeatures.grayscale)
                                            ? 'bg-red-50' 
                                            : ''
                                    } ${
                                        ((id === 'zoom-in' && zoomLevel >= 5) ||
                                        (id === 'zoom-out' && zoomLevel <= -5))
                                            ? 'opacity-50 cursor-not-allowed'
                                            : ''
                                    }`}
                                >
                                    <Icon className="text-blue-900" />
                                    <span>{label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Botones laterales derechos (accesos rápidos) */}
            <div className="fixed right-0 top-1/2 -translate-y-1/2 z-30">
                {buttonInfo.map(({ id, icon: Icon, path, label }) => (
                    <button 
                        key={id}
                        onClick={() => navigate(path)}
                        className="bg-white shadow-lg w-12 h-12 flex items-center justify-center hover:bg-red-100 transition-all duration-300 relative group border-l border-gray-100"
                        onMouseEnter={() => setHoveredButton(id)}
                        onMouseLeave={() => setHoveredButton(null)}
                    >
                        <Icon size={24} className="text-[#e7323a]" />
                        {hoveredButton === id && (
                            <div className={`${tooltipStyle} right-full mr-2`}>
                                {label}
                            </div>
                        )}
                    </button>
                ))}
            </div>
        </>
    );
};

export default SideButtons; 